import { DBB_MANDATORY_CAR_FIELDS, isCarFieldMissing } from "components/DBB/components/DBBCreateContractModal/components/DBBCarSection";
import {
  DBB_CONTRACT_TYPES,
  getContractSections,
  getIncompleteCustomerSections
} from "components/DBB/components/DBBCreateContractModal/components/DBBCreateContractForm";
import { getTrimmedValue } from "components/DBB/components/DBBCreateContractModal/components/DBBCustomerSection";
import { MINIMUM_COMPLETE_TYRE_SETS, SET_STATES, countCompleteTyreSets } from "components/DBB/components/DBBCreateContractModal/components/DBBTyreSetsSection";
import { SEASONS } from "models";

import type { DBBContractFormPayload, DBBContractSection } from "components/DBB/components/DBBCreateContractModal/components/DBBCreateContractForm";
import type { DBBContractCustomer } from "components/DBB/components/DBBCreateContractModal/components/DBBCustomerSection";
import type { DBB_TYRE_STATES } from "components/DBB/components/DBBCreateContractModal/components/DBBTyreSearch";
import type { DBBContractSetTyre, DBB_TYRE_POSITIONS, DBB_WHEEL_TYPES } from "components/DBB/components/DBBCreateContractModal/components/DBBTyreSetsSection";
import type { Car, CarID, DBBLeaseCompanyID, DBBStorageLocationID } from "models";

export enum DBB_CODE_TYPES {
  Manufacturer = "MANUFACTURER",
  Local = "LOCAL"
}

export enum DBB_CUSTOMER_TYPES {
  Owner = "O",
  Driver = "D",
  Holder = "H"
}

export enum DBB_TYRE_TYPES {
  Winter = "W",
  Summer = "S",
  AllSeason = "A"
}

const TYRE_TYPES: Record<SEASONS, DBB_TYRE_TYPES> = {
  [SEASONS.Winter]: DBB_TYRE_TYPES.Winter,
  [SEASONS.Summer]: DBB_TYRE_TYPES.Summer,
  [SEASONS.AllSeason]: DBB_TYRE_TYPES.AllSeason,
  [SEASONS.NoSeason]: DBB_TYRE_TYPES.AllSeason,
  [SEASONS.TruckSeason]: DBB_TYRE_TYPES.AllSeason
};

type DBBContractCode = {
  Value: string;
  Type: DBB_CODE_TYPES;
};

type DBBContractDescription = {
  Language: string;
  Value: string;
};

type DBBCreateContractCar = {
  License: string;
  VIN: string;
  Brand: string;
  Model: string;
  ModelYear: number | null;
  Mileage: number | null;
};

type DBBCreateContractTyre = {
  Code: DBBContractCode[];
  Position: DBB_TYRE_POSITIONS;
  State: DBB_TYRE_STATES;
  Brand: string;
  Description: DBBContractDescription[];
  TyreType: DBB_TYRE_TYPES;
  Size: number;
  Width: number;
  Height: number;
  SpeedIndicator: string;
  LoadIndex: number;
  WheelType: DBB_WHEEL_TYPES | null;
  MeasuredDepth: number | null;
};

type DBBCreateContractCustomer = {
  CustomerType: DBB_CUSTOMER_TYPES;
  Initials: string;
  Name: string;
  Street: string;
  Number: string;
  Postalcode: string;
  City: string;
  Sex: string;
  Code: DBBContractCode[];
};

export type DBBCreateContractPayload = {
  car_id: CarID;
  contract_type: DBB_CONTRACT_TYPES;
  pickup_by_dbb: boolean;
  car: DBBCreateContractCar;
  tyres: DBBCreateContractTyre[];
  lease_company_id?: DBBLeaseCompanyID;
  storage_location_id?: DBBStorageLocationID;
  driver?: DBBCreateContractCustomer;
  owner?: DBBCreateContractCustomer;
};

const toContractCode = (value: string, type: DBB_CODE_TYPES): DBBContractCode[] => (value ? [{ Value: value, Type: type }] : []);

const toCreateContractTyre = (wheelType: DBB_WHEEL_TYPES | null, state: DBB_TYRE_STATES, { tyre, position, profile }: DBBContractSetTyre): DBBCreateContractTyre => ({
  Code: toContractCode(tyre.ean ?? "", DBB_CODE_TYPES.Manufacturer),
  Position: position,
  State: state,
  Brand: tyre.manufacturer,
  Description: [{ Language: "NL", Value: tyre.description }],
  TyreType: TYRE_TYPES[tyre.season],
  Size: tyre.size,
  Width: tyre.width,
  Height: tyre.height,
  SpeedIndicator: tyre.speed,
  LoadIndex: tyre.carry_capacity,
  WheelType: wheelType,
  MeasuredDepth: profile
});

const CUSTOMER_TYPES_BY_PARTY: Record<DBBContractSection["customerField"], DBB_CUSTOMER_TYPES> = {
  driver: DBB_CUSTOMER_TYPES.Driver,
  owner: DBB_CUSTOMER_TYPES.Owner
};

const toCreateContractCustomer = (customerField: DBBContractSection["customerField"], customer: DBBContractCustomer): DBBCreateContractCustomer => ({
  CustomerType: CUSTOMER_TYPES_BY_PARTY[customerField],
  Initials: customer.initials ?? "",
  Name: getTrimmedValue(customer.name) || getTrimmedValue(customer.company),
  Street: customer.street ?? "",
  Number: customer.number,
  Postalcode: customer.postcode ?? "",
  City: customer.place ?? "",
  Sex: "U",
  Code: toContractCode(customer.dms_nr, DBB_CODE_TYPES.Local)
});

export const toCreateContractPayload = (car: Car, values: DBBContractFormPayload): DBBCreateContractPayload => {
  const payload: DBBCreateContractPayload = {
    car_id: car.id,
    contract_type: values.contract_type,
    pickup_by_dbb: values.pickup_by_dbb,
    car: {
      License: values.car.reg_nr,
      VIN: values.car.vin_nr,
      Brand: values.car.make,
      Model: values.car.model,
      ModelYear: values.car.model_year,
      Mileage: values.car.mileage
    },
    tyres: values.tyre_sets.flatMap(({ set_type, wheel_type, tyres }) => tyres.map(setTyre => toCreateContractTyre(wheel_type, SET_STATES[set_type], setTyre)))
  };

  if (values.contract_type === DBB_CONTRACT_TYPES.Lease && values.lease_company_id) payload.lease_company_id = values.lease_company_id;
  if (values.storage_location_id) payload.storage_location_id = values.storage_location_id;

  getContractSections(values.contract_type).forEach(({ customerField, unknownField }) => {
    const customer = values[customerField];
    if (values[unknownField] || !customer) return;

    payload[customerField] = toCreateContractCustomer(customerField, customer);
  });

  return payload;
};

export const getContractFormErrors = (values: DBBContractFormPayload) => ({
  hasIncompleteCar: DBB_MANDATORY_CAR_FIELDS.some(field => isCarFieldMissing(values.car[field])),
  hasIncompleteCustomer: !!getIncompleteCustomerSections(values).length,
  hasMissingLeaseCompany: values.contract_type === DBB_CONTRACT_TYPES.Lease && !values.lease_company_id,
  hasMissingStorageLocation: !values.storage_location_id,
  hasIncompleteTyreSets: countCompleteTyreSets(values.tyre_sets) < MINIMUM_COMPLETE_TYRE_SETS
});
